// BarcodeScanner: Handles both USB barcode scanner (keyboard) and camera scanning
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
	Box,
	TextField,
	Button,
	Typography,
	IconButton,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	Collapse,
} from "@mui/material";
import { CameraAltOutlined, Close } from "@mui/icons-material";
import { BrowserMultiFormatReader } from "@zxing/browser";

/**
 * BarcodeScanner supports two modes:
 * 1. USB / Bluetooth barcode scanner – treated as rapid keyboard input ending in Enter
 * 2. Camera scanning – opens a dialog with a live viewfinder; scanned items trigger a
 *    beep + alert banner without closing the dialog so an entire cart can be scanned.
 *    Uses the BarcodeDetector Web API (Chrome/Edge) when available, or falls back to
 *    @zxing/browser (works in Safari / Firefox / all modern browsers including iPhone).
 *
 * Props:
 *   onScan(value: string)    – called when a barcode is detected
 *   disabled                 – disable all scanning input
 *   placeholder              – text field placeholder
 *   lastScannedTitle         – title of the last item successfully added to the cart
 *                              (passed back from parent); drives the success alert
 *   lastScanKey              – increments on every scan event; causes the alert to
 *                              re-trigger even when the same item is scanned again
 *   onUndo()                 – called when the user clicks "Undo adding [item]"
 *   onDialogClose()          – called when the camera dialog is closed; lets the
 *                              parent clear undo state so the button doesn't persist
 *                              across scanning sessions
 */
const BarcodeScanner = ({
	onScan,
	disabled = false,
	placeholder = "Scan or type barcode…",
	lastScannedTitle = null,
	lastScanKey = 0,
	onUndo = null,
	onDialogClose = null,
}) => {
	const [inputValue, setInputValue] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [cameraError, setCameraError] = useState("");
	const [scanAlert, setScanAlert] = useState("");

	const inputRef = useRef(null);
	const videoRef = useRef(null);
	const streamRef = useRef(null);
	// When true, skip auto-focusing the text input after the dialog closes
	// (prevents the keyboard from popping up on mobile when the user taps Close)
	const skipFocusOnCloseRef = useRef(false);
	// Native BarcodeDetector scanning
	const detectorRef = useRef(null);
	const animFrameRef = useRef(null);
	// @zxing/browser scanning controls (Safari fallback)
	const zxingControlsRef = useRef(null);
	// Scan cooldown – prevents duplicate reads within 300 ms
	const scanCooldownRef = useRef(false);
	// AudioContext kept alive for the session; created on first user gesture
	const audioCtxRef = useRef(null);

	// True when the native BarcodeDetector API is available (Chrome/Edge)
	const nativeDetectorAvailable =
		typeof window !== "undefined" && "BarcodeDetector" in window;

	// Ensure AudioContext exists and is running (iOS requires user-gesture creation)
	const ensureAudioContext = () => {
		if (!audioCtxRef.current) {
			audioCtxRef.current = new (
				window.AudioContext || window.webkitAudioContext
			)();
		}
		if (audioCtxRef.current.state === "suspended") {
			audioCtxRef.current.resume();
		}
	};

	// Play a short 1 kHz confirmation beep
	const playBeep = useCallback(() => {
		const ctx = audioCtxRef.current;
		if (!ctx) return;
		try {
			const osc = ctx.createOscillator();
			const gain = ctx.createGain();
			osc.connect(gain);
			gain.connect(ctx.destination);
			osc.type = "sine";
			osc.frequency.value = 1000;
			gain.gain.setValueAtTime(0.3, ctx.currentTime);
			gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
			osc.start(ctx.currentTime);
			osc.stop(ctx.currentTime + 0.15);
		} catch (_) {}
	}, []);

	// Auto-focus the text input when the dialog is closed (USB scanner workflow).
	// Skipped when the user explicitly closes the dialog so the keyboard doesn't
	// pop up on mobile.
	useEffect(() => {
		if (!dialogOpen && inputRef.current) {
			if (skipFocusOnCloseRef.current) {
				skipFocusOnCloseRef.current = false;
				return;
			}
			inputRef.current.focus();
		}
	}, [dialogOpen]);

	// Show the scan-success alert whenever the parent signals a new scan event.
	// Keyed on lastScanKey (increments every scan) so the alert re-triggers even
	// when the same item is scanned multiple times in a row. Duration matches the
	// 1250 ms re-scan cooldown so they clear together.
	useEffect(() => {
		if (lastScanKey && lastScannedTitle) {
			setScanAlert(lastScannedTitle);
			const id = setTimeout(() => setScanAlert(""), 1250);
			return () => clearTimeout(id);
		}
	}, [lastScanKey]); // eslint-disable-line react-hooks/exhaustive-deps

	// USB scanner submits on Enter key
	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			const val = inputValue.trim();
			if (val) {
				onScan(val);
				setInputValue("");
			}
		}
	};

	// Camera scan handler: enforces cooldown, plays beep, notifies parent
	const handleCameraScan = useCallback(
		(value) => {
			if (scanCooldownRef.current) return;
			scanCooldownRef.current = true;
			playBeep();
			onScan(value);
			setTimeout(() => {
				scanCooldownRef.current = false;
			}, 1250);
		},
		[onScan, playBeep],
	);

	const stopCamera = useCallback(() => {
		// Stop native BarcodeDetector loop
		if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
		animFrameRef.current = null;
		detectorRef.current = null;

		// Stop @zxing/browser controls
		if (zxingControlsRef.current) {
			try {
				zxingControlsRef.current.stop();
			} catch (_) {}
			zxingControlsRef.current = null;
		}

		// Release camera stream
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		}
	}, []);

	// --- Native BarcodeDetector path (Chrome/Edge) ---
	const startNativeScanning = useCallback(async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: "environment" } },
			});
			streamRef.current = stream;
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				// autoPlay attribute handles play() on iOS; call play() as well for
				// browsers that don't honour autoPlay on srcObject assignment
				videoRef.current.play().catch(() => {});
			}

			// eslint-disable-next-line no-undef
			const detector = new BarcodeDetector({
				formats: [
					"code_128",
					"code_39",
					"ean_13",
					"ean_8",
					"qr_code",
					"upc_a",
					"upc_e",
				],
			});
			detectorRef.current = detector;

			const scan = async () => {
				if (!videoRef.current || !detectorRef.current) return;
				try {
					const barcodes = await detectorRef.current.detect(videoRef.current);
					if (barcodes.length > 0 && !scanCooldownRef.current) {
						handleCameraScan(barcodes[0].rawValue);
					}
				} catch (_) {
					// detection frame error – continue
				}
				animFrameRef.current = requestAnimationFrame(scan);
			};
			animFrameRef.current = requestAnimationFrame(scan);
		} catch (err) {
			setCameraError(err.message || "Camera access denied");
		}
	}, [handleCameraScan]);

	// --- @zxing/browser path (Safari / Firefox / all other browsers) ---
	const startZxingScanning = useCallback(async () => {
		try {
			const codeReader = new BrowserMultiFormatReader();
			// decodeFromConstraints lets us control facingMode directly, which is
			// required for iOS to reliably activate the rear camera.
			const controls = await codeReader.decodeFromConstraints(
				{ video: { facingMode: { ideal: "environment" } } },
				videoRef.current,
				(result) => {
					if (result) {
						handleCameraScan(result.getText());
					}
					// Non-result callbacks are normal "no barcode yet" states — ignore
				},
			);
			zxingControlsRef.current = controls;
			// Keep a handle to the stream so stopCamera can release the tracks
			if (videoRef.current && videoRef.current.srcObject) {
				streamRef.current = videoRef.current.srcObject;
			}
		} catch (err) {
			setCameraError(err.message || "Camera access denied");
		}
	}, [handleCameraScan]);

	// Start camera when dialog opens; stop when it closes.
	// A 250 ms delay lets the MUI Dialog enter-transition (225 ms) fully complete
	// so the video element is in the DOM before getUserMedia / play() is called.
	// This is critical on iOS Safari where play() must be called while the element
	// is fully mounted; without the delay the viewfinder renders black.
	useEffect(() => {
		if (!dialogOpen) {
			stopCamera();
			return () => {};
		}
		setCameraError("");
		scanCooldownRef.current = false;
		const timer = setTimeout(() => {
			if (nativeDetectorAvailable) {
				startNativeScanning();
			} else {
				startZxingScanning();
			}
		}, 250);
		return () => {
			clearTimeout(timer);
			stopCamera();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialogOpen]);

	const openDialog = () => {
		ensureAudioContext();
		setDialogOpen(true);
	};

	const closeDialog = () => {
		skipFocusOnCloseRef.current = true;
		setDialogOpen(false);
		setScanAlert("");
		onDialogClose?.();
	};

	return (
		<Box>
			{/* Text / USB input */}
			<Box display="flex" alignItems="center" gap={1}>
				<TextField
					inputRef={inputRef}
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={disabled}
					size="small"
					fullWidth
					autoComplete="off"
					inputProps={{ "aria-label": "barcode input" }}
				/>
				<Button
					variant="outlined"
					size="small"
					disabled={disabled || !inputValue.trim()}
					onClick={() => {
						const val = inputValue.trim();
						if (val) {
							onScan(val);
							setInputValue("");
						}
					}}
				>
					Go
				</Button>
				<Tooltip title="Scan with camera">
					<span>
						<IconButton onClick={openDialog} disabled={disabled} size="small">
							<CameraAltOutlined />
						</IconButton>
					</span>
				</Tooltip>
			</Box>

			{/* Camera scanner dialog (stays open after each scan) */}
			<Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
				<DialogTitle
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						pb: 1,
					}}
				>
					<Typography variant="h6">Scan Barcode</Typography>
					<IconButton
						onClick={closeDialog}
						size="small"
						aria-label="close scanner"
					>
						<Close />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ p: 0 }}>
					{/* Scan-success alert banner */}
					<Collapse in={!!scanAlert}>
						<Alert severity="success" sx={{ mx: 2, mt: 1 }}>
							Added: <strong>{scanAlert}</strong>
						</Alert>
					</Collapse>

					{/* Live viewfinder */}
					<Box sx={{ position: "relative", bgcolor: "black", mt: 1 }}>
						<video
							ref={videoRef}
							autoPlay
							muted
							playsInline
							style={{ width: "100%", display: "block", maxHeight: 360 }}
						/>
						{/* Targeting guide overlay */}
						<Box
							sx={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
								width: "70%",
								height: 72,
								border: "2px solid rgba(255,255,255,0.65)",
								borderRadius: 1,
								pointerEvents: "none",
							}}
						/>
					</Box>

					{cameraError && (
						<Alert severity="error" sx={{ mx: 2, mt: 1 }}>
							{cameraError}
						</Alert>
					)}

					<Box sx={{ px: 2, py: 1 }}>
						<Typography variant="caption" color="text.secondary">
							Point the camera at a barcode. Items are added automatically — the
							scanner stays open so you can scan a full cart.
						</Typography>
					</Box>
				</DialogContent>

				<DialogActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
					{onUndo && lastScannedTitle ? (
						<Button
							variant="outlined"
							color="warning"
							size="small"
							onClick={() => {
								onUndo();
								setScanAlert("");
							}}
						>
							Undo adding {lastScannedTitle}
						</Button>
					) : (
						<Box />
					)}
					<Button
						onClick={closeDialog}
						variant="contained"
						color="error"
						size="small"
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default BarcodeScanner;
